type UserPrefsV1 = {
  // only needs to be incremented for breaking changes
  version: 1;
  queryList: {
    showRead: boolean;
    showWrite: boolean;
    showOther: boolean;
    doCompare: boolean;
  };
  serverSelect: {
    recentServerIds: { [organizationId: string]: string[] };
  };
  databaseSelect: {
    recentDbIds: { [serverHumanId: string]: string[] };
  };
  lastVisitedOrg: string;
  flash: {
    dismissed: {
      id: string;
      ts: number;
    }[];
  };
  indexAdvisor: {
    hideAcknowledged: boolean;
  };
  explain: {
    lastView: string;
  };
  gridHiddenColumns: {
    [gridId: string]: string[];
  };
};

type UserPrefsV2 = Omit<UserPrefsV1, "lastVisitedOrg" | "version"> & {
  version: 2;
  lastVisitedOrg?: {
    id: string;
    slug: string;
  };
  logInsights?: {
    expandedCategories: string[];
  };
};

type UserPrefsV3 = Omit<UserPrefsV2, "version"> & {
  version: 3;
  databaseGroupSelect: {
    recentDatnames: { [serverGroupId: string]: string[] };
  };
};

export type RecentItem = {
  type: "server" | "serverGroup";
  id: string;
  // For a server selected as a member of a group, the id of that group, so recents can render it
  // under the group it was clicked from. Absent = selected standalone.
  parentServerGroupId?: string;
};

type UserPrefsV4 = Omit<UserPrefsV3, "serverSelect" | "version"> & {
  version: 4;
  serverSelect: {
    recentItems: { [organizationId: string]: RecentItem[] };
    collapsedSections?: string[];
  };
};

// Discriminated union of all versions
export type StoredUserPreferences =
  | UserPrefsV1
  | UserPrefsV2
  | UserPrefsV3
  | UserPrefsV4;

// Always the latest value
export type LatestUserPreferences = UserPrefsV4;

// The exposed type hides the version; that's internal to encoding/decoding
export type UserPreferences = Omit<LatestUserPreferences, "version">;

const serverSelectFromLegacy = (
  old: { recentServerIds?: { [organizationId: string]: string[] } } | undefined,
): UserPrefsV4["serverSelect"] => {
  const recentItems: { [organizationId: string]: RecentItem[] } = {};
  for (const [orgId, ids] of Object.entries(old?.recentServerIds ?? {})) {
    recentItems[orgId] = ids.map((id) => ({ type: "server", id }));
  }
  return { recentItems };
};

// Take whatever's stored and convert to latest version. We
// can age out old versions if we're confident they are no
// longer in use.
export const convertPreferences = (
  prefs: StoredUserPreferences,
): UserPreferences => {
  switch (prefs.version) {
    case 1: {
      const { version: _v1, serverSelect, ...v1rest } = prefs;
      return {
        ...v1rest,
        // We could fill in the slug from the last visited org, but we'd still
        // have a null id; just drop it
        lastVisitedOrg: undefined,
        serverSelect: serverSelectFromLegacy(serverSelect),
        databaseGroupSelect: { recentDatnames: {} },
      };
    }
    case 2: {
      const { version: _v2, serverSelect, ...v2rest } = prefs;
      return {
        ...v2rest,
        serverSelect: serverSelectFromLegacy(serverSelect),
        databaseGroupSelect: { recentDatnames: {} },
      };
    }
    case 3: {
      const { version: _v3, serverSelect, ...v3rest } = prefs;
      return {
        ...v3rest,
        serverSelect: serverSelectFromLegacy(serverSelect),
      };
    }
    case 4: {
      const { version: _v4, ...v4rest } = prefs;
      return v4rest;
    }
  }
};
